import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Users, Package, IndianRupee, Calendar, Gift, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import WeddingCard from "@/components/WeddingCard";
import CreateWeddingDialog from "@/components/CreateWeddingDialog";
import EnhancedAppHeader from "@/components/EnhancedAppHeader";
import { UpgradeModal } from "@/components/UpgradeModal";
import { useCurrency } from "@/hooks/useCurrency";
import { useAuth } from "@/contexts/AuthContext";
import { permissions } from "@/lib/mockAuth";
import { weddingService } from "@/services/wedding.service";
import { authService } from "@/services/auth.service";
import { toast } from "sonner";
import { isTrialWedding, canCreateWedding } from "@/utils/trialUtils";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

export default function Index() {
  const [weddings, setWeddings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const navigate = useNavigate();
  const { userRole, activeOrg, switchOrganization } = useAuth();
  const { formatCurrency } = useCurrency();

  const canCreateWeddings = permissions.canCreateWeddings(userRole);
  const isClient = userRole === 'client' || userRole === 'read_only_client';
  const [blockedDialogOpen, setBlockedDialogOpen] = useState(false);
  
  // Check trial/credit status - use useMemo to recalculate when dependencies change
  const trialWedding = useMemo(() => weddings.find(w => isTrialWedding(w)), [weddings]);
  const weddingCreationCheck = useMemo(() => canCreateWedding(weddings, activeOrg), [weddings, activeOrg?.credits, activeOrg?.trial_wedding_id, activeOrg?.trial_started_at]);

  useEffect(() => {
    // If client, redirect to their wedding directly (immediately, no loading state)
    if (isClient && activeOrg?.id) {
      loadClientWedding();
    } else if (activeOrg?.id) {
      loadWeddings();
    }
  }, [activeOrg, isClient]);

  const loadClientWedding = async () => {
    if (!activeOrg?.id) return;
    
    try {
      const weddings = await weddingService.getMyWeddings();
      if (weddings && weddings.length > 0) {
        // Redirect immediately to the first (and only) wedding
        navigate(`/wedding/${weddings[0].id}`, { replace: true });
      } else {
        // No wedding assigned yet - show message
        setWeddings([]);
        setLoading(false);
      }
    } catch (error) {
      console.error('Failed to load client wedding:', error);
      toast.error('Failed to load wedding information');
      setLoading(false);
    }
  };

  const loadWeddings = async () => {
    if (!activeOrg?.id) return;
    
    try {
      setLoading(true);
      const data = await weddingService.getWeddings(activeOrg.id);
      setWeddings(data);
    } catch (error) {
      console.error('Failed to load weddings:', error);
      toast.error('Failed to load weddings');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateWedding = async (newWeddingData) => {
    try {
      await weddingService.createWedding(activeOrg.id, newWeddingData);
      await loadWeddings();
      
      // Refresh organization data to get updated credits
      // This will update the button state immediately
      try {
        const orgs = await authService.getOrganizations();
        const updatedOrg = orgs.find(o => o.id === activeOrg.id);
        if (updatedOrg) {
          // Update activeOrg in localStorage
          localStorage.setItem('activeOrg', JSON.stringify(updatedOrg));
          // The useMemo will recalculate weddingCreationCheck when activeOrg changes
          // We need to trigger a re-render by updating state or reloading
          // For now, reload after a short delay to ensure UI updates
          setTimeout(() => {
            window.location.reload();
          }, 1500);
        }
      } catch (refreshError) {
        console.error('Failed to refresh organization data:', refreshError);
        // Still reload to ensure fresh data
        setTimeout(() => {
          window.location.reload();
        }, 1500);
      }
      
      toast.success('Wedding created successfully!');
    } catch (error) {
      console.error('Failed to create wedding:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to create wedding';
      toast.error(errorMessage);
      
      // If it's a credit/trial error, the error message will guide user to upgrade
      if (errorMessage.includes('credits') || errorMessage.includes('Trial') || errorMessage.includes('Upgrade')) {
        // Error message already contains upgrade guidance
      }
    }
  };

  // Calculate stats
  const activeWeddings = weddings.filter(w => w.status === "planning" || w.status === "active").length;
  const totalGuests = weddings.reduce((sum, w) => sum + (w.guest_count || 0), 0);
  const totalVendors = weddings.reduce((sum, w) => sum + (w.vendor_count || 0), 0);
  const totalBudget = weddings.reduce((sum, w) => sum + (w.budget || 0), 0);

  // If client is loading, show loading state
  if (isClient && loading) {
    return (
      <div className="min-h-screen flex items-center justify-center gradient-soft">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading your wedding...</p>
        </div>
      </div>
    );
  }

  // If client has no wedding assigned
  if (isClient && !loading && weddings.length === 0) {
    return (
      <div className="min-h-screen gradient-soft">
        <EnhancedAppHeader
          title="Welcome"
          subtitle="Your wedding information"
        />
        <div className="container mx-auto px-4 py-12">
          <div className="bg-card rounded-lg p-12 text-center shadow-soft max-w-2xl mx-auto">
            <Calendar className="h-16 w-16 mx-auto text-muted-foreground/50 mb-4" />
            <h3 className="text-xl font-semibold mb-2">No Wedding Assigned</h3>
            <p className="text-muted-foreground mb-6">
              You haven't been assigned to any wedding yet. Please contact your planner to get access to your wedding details.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen gradient-soft">
      {/* Enhanced Header with Profile Menu */}
      <EnhancedAppHeader
        title="Your Weddings"
        subtitle="Manage all your wedding projects"
        action={
          canCreateWeddings && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span>
                    <Button 
                      onClick={() => {
                        if (!weddingCreationCheck.canCreate) {
                          setBlockedDialogOpen(true);
                        } else {
                          setIsDialogOpen(true);
                        }
                      }}
                      className="gradient-primary hover:shadow-medium transition-smooth"
                      size="lg"
                      disabled={!weddingCreationCheck.canCreate}
                    >
                      <Plus className="h-5 w-5" />
                      <span className="hidden sm:inline">New Wedding</span>
                      <span className="sm:hidden">New</span>
                    </Button>
                  </span>
                </TooltipTrigger>
                {!weddingCreationCheck.canCreate && (
                  <TooltipContent className="bg-yellow-600 text-white border-yellow-600 max-w-xs">
                    <p className="font-medium">{weddingCreationCheck.message || 'Upgrade to create more weddings'}</p>
                  </TooltipContent>
                )}
              </Tooltip>
            </TooltipProvider>
          )
        }
      />

      {/* Trial/Credits Banner */}
      <div className="container mx-auto px-4 py-6">
        {trialWedding && !trialWedding.trial_expired && (
          <Alert className="border-blue-500 bg-blue-50 dark:bg-blue-900/20 mb-4">
            <Gift className="h-4 w-4 text-blue-600 dark:text-blue-400" />
            <AlertDescription>
              <strong className="text-blue-900 dark:text-blue-100">🎁 You're on a free trial</strong>
              <p className="text-sm text-blue-800 dark:text-blue-200 mt-1">
                1 wedding • Limited features • 7 days
              </p>
            </AlertDescription>
          </Alert>
        )}
        {(!activeOrg?.credits || activeOrg.credits === 0) && !trialWedding && (
          <Alert className="border-orange-500 bg-orange-50 dark:bg-orange-900/20 mb-4">
            <AlertTriangle className="h-4 w-4 text-orange-600 dark:text-orange-400" />
            <AlertDescription>
              <strong className="text-orange-900 dark:text-orange-100">⚠️ No wedding credits available</strong>
              <p className="text-sm text-orange-800 dark:text-orange-200 mt-1">
                Purchase a wedding credit to create a new wedding.
              </p>
            </AlertDescription>
          </Alert>
        )}
      </div>

      {/* Stats Grid */}
      <div className="container mx-auto px-4 py-6 sm:py-8">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <div className="bg-card rounded-lg p-4 sm:p-6 shadow-soft border-t-2 border-primary transition-smooth hover:shadow-medium">
            <div className="flex items-center gap-3">
              <div className="p-3 rounded-lg bg-primary/10">
                <Calendar className="h-5 w-5 sm:h-6 sm:w-6 text-primary" />
              </div>
              <div>
                <p className="text-2xl sm:text-3xl font-bold text-primary">{activeWeddings}</p>
                <p className="text-xs sm:text-sm text-muted-foreground">Active Weddings</p>
              </div>
            </div>
          </div>

          <div className="bg-card rounded-lg p-4 sm:p-6 shadow-soft border-t-2 border-accent transition-smooth hover:shadow-medium">
            <div className="flex items-center gap-3">
              <div className="p-3 rounded-lg bg-accent/10">
                <Users className="h-5 w-5 sm:h-6 sm:w-6 text-accent" />
              </div>
              <div>
                <p className="text-2xl sm:text-3xl font-bold text-accent">{totalGuests.toLocaleString()}</p>
                <p className="text-xs sm:text-sm text-muted-foreground">Total Guests</p>
              </div>
            </div>
          </div>

          <div className="bg-card rounded-lg p-4 sm:p-6 shadow-soft border-t-2 border-gold transition-smooth hover:shadow-medium">
            <div className="flex items-center gap-3">
              <div className="p-3 rounded-lg bg-gold/10">
                <Package className="h-5 w-5 sm:h-6 sm:w-6 text-gold" />
              </div>
              <div>
                <p className="text-2xl sm:text-3xl font-bold text-gold">{totalVendors}</p>
                <p className="text-xs sm:text-sm text-muted-foreground">Total Vendors</p>
              </div>
            </div>
          </div>

          <div className="bg-card rounded-lg p-4 sm:p-6 shadow-soft border-t-2 border-success transition-smooth hover:shadow-medium">
            <div className="flex items-center gap-3">
              <div className="p-3 rounded-lg bg-success/10">
                <IndianRupee className="h-5 w-5 sm:h-6 sm:w-6 text-success" />
              </div>
              <div>
                <p className="text-2xl sm:text-3xl font-bold text-success">{formatCurrency(totalBudget)}</p>
                <p className="text-xs sm:text-sm text-muted-foreground">Total Budget</p>
              </div>
            </div>
          </div>
        </div>

        {/* Wedding Cards Grid */}
        <div>
          <h2 className="text-2xl font-bold mb-4 text-foreground">Your Weddings</h2>
          {weddings.length === 0 ? (
            <div className="bg-card rounded-lg p-12 text-center shadow-soft">
              <Calendar className="h-16 w-16 mx-auto text-muted-foreground/50 mb-4" />
              <h3 className="text-xl font-semibold mb-2">No weddings yet</h3>
              <p className="text-muted-foreground mb-6">Create your first wedding to get started</p>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span>
                      <Button 
                        onClick={() => {
                          if (!weddingCreationCheck.canCreate) {
                            setBlockedDialogOpen(true);
                          } else {
                            setIsDialogOpen(true);
                          }
                        }}
                        className="gradient-primary"
                        disabled={!weddingCreationCheck.canCreate}
                      >
                        <Plus className="h-5 w-5" />
                        Create Wedding
                      </Button>
                    </span>
                  </TooltipTrigger>
                  {!weddingCreationCheck.canCreate && (
                    <TooltipContent className="bg-yellow-600 text-white border-yellow-600 max-w-xs">
                      <p className="font-medium">{weddingCreationCheck.message || 'Upgrade to create more weddings'}</p>
                    </TooltipContent>
                  )}
                </Tooltip>
              </TooltipProvider>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {weddings.map((wedding) => (
                <WeddingCard key={wedding.id} wedding={wedding} onUpdate={loadWeddings} />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Create Wedding Dialog */}
      <CreateWeddingDialog 
        open={isDialogOpen}
        onOpenChange={setIsDialogOpen}
        onCreateWedding={handleCreateWedding}
      />

      {/* Upgrade Modal for blocked wedding creation */}
      <UpgradeModal
        wedding={null}
        open={blockedDialogOpen}
        onOpenChange={setBlockedDialogOpen}
        onUpgradeSuccess={() => {
          setBlockedDialogOpen(false);
          loadWeddings();
        }}
      />
    </div>
  );
}